import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { prayerTimesAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Clock, Sun, Sunrise, Sunset, Moon } from 'lucide-react';

const prayerIcons = {
  fajr: Sunrise,
  sunrise: Sun,
  dhuhr: Sun,
  asr: Sun,
  maghrib: Sunset,
  isha: Moon,
};

const PrayerTimesWidget = () => {
  const { t, lang } = useLanguage();
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrayerTimes = async () => {
      try {
        const data = await prayerTimesAPI.get();
        setPrayerTimes(data);
      } catch (error) {
        console.error('Failed to fetch prayer times:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrayerTimes();
  }, []);

  useEffect(() => {
    if (!prayerTimes) return;

    const calculateNextPrayer = () => {
      const now = new Date();
      const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
      
      for (const prayer of prayers) {
        const [hours, minutes] = prayerTimes[prayer].split(':').map(Number);
        const prayerTime = new Date();
        prayerTime.setHours(hours, minutes, 0, 0);
        
        if (prayerTime > now) {
          setNextPrayer(prayer);
          return prayerTime;
        }
      }
      
      // If all prayers passed, next is Fajr tomorrow
      setNextPrayer('fajr');
      const [hours, minutes] = prayerTimes.fajr.split(':').map(Number);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(hours, minutes, 0, 0);
      return tomorrow;
    };

    const updateCountdown = () => {
      const nextPrayerTime = calculateNextPrayer();
      const now = new Date();
      const diff = nextPrayerTime - now;
      
      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ hours, minutes, seconds });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [prayerTimes]);

  if (loading) {
    return (
      <Card className="bg-white border-stone-200 shadow-lg">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-stone-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-10 bg-stone-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prayerTimes) return null;

  const prayers = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

  return (
    <Card className="bg-white border-stone-200 shadow-lg overflow-hidden" data-testid="prayer-times-widget">
      {/* Header with next prayer */}
      <div className="bg-[#044F5B] text-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-semibold">{t('home.prayerTimes')}</h3>
          <Clock className="w-5 h-5 opacity-80" />
        </div>
        
        {nextPrayer && (
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm opacity-80 mb-1">{t('home.nextPrayer')}</p>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-2xl font-semibold capitalize">
                  {t(`prayers.${nextPrayer}`)}
                </span>
                <span className="text-xl ml-3 opacity-90">{prayerTimes[nextPrayer]}</span>
              </div>
              <div className="text-right animate-pulse-gentle" data-testid="prayer-countdown">
                <span className="text-sm opacity-80">{t('prayers.countdown')}</span>
                <div className="font-mono text-lg">
                  <span>{String(countdown.hours).padStart(2, '0')}</span>
                  <span className="opacity-60">:</span>
                  <span>{String(countdown.minutes).padStart(2, '0')}</span>
                  <span className="opacity-60">:</span>
                  <span>{String(countdown.seconds).padStart(2, '0')}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Prayer times list */}
      <CardContent className="p-0">
        <ul className="divide-y divide-stone-100">
          {prayers.map((prayer) => {
            const Icon = prayerIcons[prayer];
            const isNext = prayer === nextPrayer;
            
            return (
              <li
                key={prayer}
                className={`flex items-center justify-between px-6 py-4 transition-colors ${
                  isNext ? 'bg-islamic-teal/5' : 'hover:bg-stone-50'
                }`}
                data-testid={`prayer-time-${prayer}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${isNext ? 'text-islamic-teal' : 'text-stone-400'}`} />
                  <span className={`font-medium ${isNext ? 'text-islamic-teal' : 'text-stone-700'}`}>
                    {t(`prayers.${prayer}`)}
                  </span>
                </div>
                <span className={`font-mono text-lg ${isNext ? 'text-islamic-teal font-semibold' : 'text-stone-600'}`}>
                  {prayerTimes[prayer]}
                </span>
              </li>
            );
          })}
        </ul>
        
        {/* Hijri date */}
        {prayerTimes.hijri_date && (
          <div className="px-6 py-3 bg-stone-50 border-t border-stone-100">
            <p className="text-sm text-stone-500 text-center font-arabic">
              {prayerTimes.hijri_date}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrayerTimesWidget;
